import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
	Flex,
	Heading,
	Text,
	Button,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Input,
	Select,
	Badge,
	useColorModeValue,
	IconButton,
	Box,
	useToast,
	InputGroup,
	InputLeftElement,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	VStack,
	HStack,
	SimpleGrid,
	Tooltip,
	Textarea,
	useDisclosure,
	FormControl,
	FormLabel,
	Divider,
	AlertDialog,
	AlertDialogBody,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogContent,
	AlertDialogOverlay,
	Spinner,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faSearch,
	faEye,
	faLifeRing,
	faInbox,
	faCheckCircle,
	faReply,
	faTrash,
	faExclamationTriangle,
	faSyncAlt,
	faMobileScreen,
	faBug,
	faPlayCircle,
	faGraduationCap,
	faBookOpen,
	faQuestionCircle
} from "@fortawesome/free-solid-svg-icons";
import { fetchTicketsFromDynamo, resolveTicketInDynamo, deleteTicketFromDynamo, Ticket } from "@/functions/ticketService";
import { apiPostRequest } from "@/functions/requests";
import { useDynamoClient } from "@/hooks/contextHooks";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";

export default function SupportTickets() {
	const dynamo = useDynamoClient() as DynamoDBClient;
	const { user } = useAuthenticator();

	const [tickets, setTickets] = useState<Ticket[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("All");
	const [categoryFilter, setCategoryFilter] = useState("All");

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
	const [replyMessage, setReplyMessage] = useState("");
	const [isSubmittingReply, setIsSubmittingReply] = useState(false);

	// Image Zoom Overlay State
	

	// Delete Confirmation States
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
	const [ticketToDelete, setTicketToDelete] = useState<string | null>(null);
	const cancelRef = useRef<HTMLButtonElement>(null);

	const toast = useToast();
	const bg = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.700");

	// Load tickets from DynamoDB
	const loadTickets = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await fetchTicketsFromDynamo(dynamo);
			setTickets(data);
		} catch (err) {
			console.error("Failed to load tickets from DynamoDB:", err);
			toast({
				title: "Error loading tickets",
				description: "Could not fetch tickets from DynamoDB. Please try again.",
				status: "error",
				duration: 3000,
				isClosable: true
			});
		} finally {
			setIsLoading(false);
		}
	}, [dynamo, toast]);

	useEffect(() => {
		loadTickets();
	}, [loadTickets]);

	const handleDeleteClick = (ticketId: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setTicketToDelete(ticketId);
		onDeleteOpen();
	};

	const handleDeleteConfirm = async () => {
		if (ticketToDelete) {
			const success = await deleteTicketFromDynamo(dynamo, ticketToDelete);
			if (success) {
				setTickets(prev => prev.filter(t => t.id !== ticketToDelete));
				toast({
					title: "Ticket Deleted",
					description: `Ticket #${ticketToDelete} has been successfully deleted from AWS.`,
					status: "success",
					duration: 3000,
					isClosable: true
				});
			} else {
				toast({
					title: "Delete Error",
					description: "Could not delete ticket from DynamoDB.",
					status: "error",
					duration: 3000,
					isClosable: true
				});
			}
			setTicketToDelete(null);
			onDeleteClose();
		}
	};

	const filteredTickets = useMemo(() => {
		return tickets.filter(t => {
			const matchesSearch =
				(t.email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
				(t.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
				(t.subject || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
				(t.id || "").toLowerCase().includes(searchQuery.toLowerCase());
			const matchesStatus = statusFilter === "All" || t.status === statusFilter;
			const matchesCategory = categoryFilter === "All" || t.category === categoryFilter;
			return matchesSearch && matchesStatus && matchesCategory;
		});
	}, [tickets, searchQuery, statusFilter, categoryFilter]);

	const stats = useMemo(() => {
		const total = tickets.length;
		const pending = tickets.filter(t => t.status === "Pending" || t.status === "In Progress").length;
		const resolved = tickets.filter(t => t.status === "Resolved").length;
		return { total, pending, resolved };
	}, [tickets]);

	const handleViewTicket = (ticket: Ticket) => {
		setSelectedTicket(ticket);
		setReplyMessage("");
		onOpen();
	};

	const handleSendReply = async () => {
		if (!selectedTicket) return;
		if (!replyMessage.trim()) {
			toast({
				title: "Error",
				description: "Reply message cannot be empty.",
				status: "error",
				duration: 3000,
				isClosable: true
			});
			return;
		}

		setIsSubmittingReply(true);
		const adminName = user?.username || (user as any)?.signInDetails?.loginId || "XNav Support";

		try {
			// 1. Resolve ticket in DynamoDB
			const success = await resolveTicketInDynamo(dynamo, selectedTicket.id, replyMessage.trim(), adminName);
			if (!success) {
				throw new Error("Failed to update ticket status in DynamoDB");
			}

			// 2. Dispatch Amazon SES resolution email via backend broadcast endpoint
			try {
				await apiPostRequest('/users/email_broadcast', {
					senderEmail: "support@xnav.studio",
					subject: `[xNav Support] Ticket Resolved: #${selectedTicket.id} - ${selectedTicket.subject}`,
					message: `Hello ${selectedTicket.name},\n\nYour support ticket #${selectedTicket.id} has been resolved by our engineering team.\n\nTicket Summary: ${selectedTicket.subject}\n\nResolution Details:\n${replyMessage}\n\nIf you have any further questions or if the issue persists, please reply directly to this email.\n\nBest regards,\nThe xNav Support Team\nsupport@xnav.studio`,
					targetAudience: "specific",
					targetValues: [selectedTicket.email]
				});
				console.log(`[SupportTickets] SES resolution email dispatched to ${selectedTicket.email}`);
			} catch (emailErr) {
				console.warn("[SupportTickets] Amazon SES email dispatch warning:", emailErr);
			}

			// 3. Dispatch Push Notification to Mobile App via backend endpoint
			try {
				await fetch('https://dmeg7vtv7k.execute-api.us-west-2.amazonaws.com/Prod/notifications/send', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						studentEmail: selectedTicket.email,
						title: "Support Ticket Resolved",
						message: `Ticket #${selectedTicket.id} has been addressed: ${replyMessage.substring(0, 120)}`
					})
				});
				console.log(`[SupportTickets] Mobile push notification dispatched for ${selectedTicket.email}`);
			} catch (pushErr) {
				console.warn("[SupportTickets] Mobile push notification dispatch warning:", pushErr);
			}

			// 4. Update local state & notify admin
			setTickets(prev =>
				prev.map(t =>
					t.id === selectedTicket.id
						? {
								...t,
								status: "Resolved",
								reply: replyMessage.trim(),
								resolvedAt: new Date().toISOString(),
								resolvedBy: adminName
						  }
						: t
				)
			);

			toast({
				title: "Ticket Resolved & Synced",
				description: `Resolution email sent to ${selectedTicket.email} and ticket marked resolved in AWS.`,
				status: "success",
				duration: 4000,
				isClosable: true
			});

			onClose();
		} catch (error) {
			console.error("Error resolving ticket:", error);
			toast({
				title: "Error",
				description: "Failed to resolve ticket in database. Please try again.",
				status: "error",
				duration: 3000,
				isClosable: true
			});
		} finally {
			setIsSubmittingReply(false);
		}
	};

	const getCategoryBadge = (category: string) => {
		switch (category) {
			case 'bug':
				return <Badge colorScheme="red" variant="subtle" borderRadius="md" px={2}><FontAwesomeIcon icon={faBug} style={{ marginRight: '4px' }} /> Bug</Badge>;
			case 'video':
				return <Badge colorScheme="blue" variant="subtle" borderRadius="md" px={2}><FontAwesomeIcon icon={faPlayCircle} style={{ marginRight: '4px' }} /> Video</Badge>;
			case 'quiz':
				return <Badge colorScheme="purple" variant="subtle" borderRadius="md" px={2}><FontAwesomeIcon icon={faGraduationCap} style={{ marginRight: '4px' }} /> Quiz</Badge>;
			case 'content':
				return <Badge colorScheme="teal" variant="subtle" borderRadius="md" px={2}><FontAwesomeIcon icon={faBookOpen} style={{ marginRight: '4px' }} /> Content</Badge>;
			default:
				return <Badge colorScheme="gray" variant="subtle" borderRadius="md" px={2}><FontAwesomeIcon icon={faQuestionCircle} style={{ marginRight: '4px' }} /> General</Badge>;
		}
	};

	return (
		<Flex direction="column" w="100%" p={8} bg={useColorModeValue("gray.50", "gray.900")} minH="100vh" overflowY="auto">
			{/* Header */}
			<Flex justify="space-between" align="center" mb={6}>
				<VStack align="left" spacing={1}>
					<HStack spacing={3}>
						<Heading size="lg" fontWeight="black" color={useColorModeValue("green.700", "green.300")}>
							Mobile Support Tickets
						</Heading>
						<Badge colorScheme="green" variant="solid" borderRadius="full" px={3} py={0.5} fontSize="xs">
							Live AWS Sync
						</Badge>
					</HStack>
					<Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
						View real-time diagnostic reports submitted from iOS and Android devices, respond to students, and dispatch resolution notifications.
					</Text>
				</VStack>

				<HStack spacing={3}>
					<Button
						size="sm"
						variant="outline"
						leftIcon={<FontAwesomeIcon icon={faSyncAlt} />}
						isLoading={isLoading}
						onClick={loadTickets}
						borderRadius="lg"
					>
						Refresh
					</Button>
				</HStack>
			</Flex>

			{/* Metric KPI Cards */}
			<SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
				<Box p={5} borderRadius="2xl" bg={bg} border="1px solid" borderColor={borderColor} shadow="sm">
					<Flex justify="space-between" align="center">
						<VStack align="left" spacing={0}>
							<Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">
								Total Reported
							</Text>
							<Heading size="xl" fontWeight="black">{stats.total}</Heading>
						</VStack>
						<Box p={3} borderRadius="xl" bg="blue.50" color="blue.500" _dark={{ bg: "rgba(66, 153, 225, 0.15)" }}>
							<FontAwesomeIcon icon={faInbox} size="lg" />
						</Box>
					</Flex>
				</Box>

				<Box p={5} borderRadius="2xl" bg={bg} border="1px solid" borderColor={borderColor} shadow="sm">
					<Flex justify="space-between" align="center">
						<VStack align="left" spacing={0}>
							<Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">
								Open / Pending
							</Text>
							<Heading size="xl" fontWeight="black" color="orange.500">{stats.pending}</Heading>
						</VStack>
						<Box p={3} borderRadius="xl" bg="orange.50" color="orange.500" _dark={{ bg: "rgba(237, 137, 54, 0.15)" }}>
							<FontAwesomeIcon icon={faLifeRing} size="lg" />
						</Box>
					</Flex>
				</Box>

				<Box p={5} borderRadius="2xl" bg={bg} border="1px solid" borderColor={borderColor} shadow="sm">
					<Flex justify="space-between" align="center">
						<VStack align="left" spacing={0}>
							<Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">
								Resolved Tickets
							</Text>
							<Heading size="xl" fontWeight="black" color="green.500">{stats.resolved}</Heading>
						</VStack>
						<Box p={3} borderRadius="xl" bg="green.50" color="green.500" _dark={{ bg: "rgba(72, 187, 120, 0.15)" }}>
							<FontAwesomeIcon icon={faCheckCircle} size="lg" />
						</Box>
					</Flex>
				</Box>
			</SimpleGrid>

			{/* Filter & Search Bar */}
			<Flex
				p={4}
				borderRadius="2xl"
				bg={bg}
				border="1px solid"
				borderColor={borderColor}
				mb={6}
				gap={4}
				align="center"
				justify="space-between"
				flexWrap="wrap"
				shadow="sm"
			>
				<InputGroup maxW={{ base: "100%", md: "380px" }}>
					<InputLeftElement pointerEvents="none">
						<FontAwesomeIcon icon={faSearch} color="gray" />
					</InputLeftElement>
					<Input
						placeholder="Search by ticket ID, user email, or subject..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						borderRadius="xl"
						bg={useColorModeValue("gray.50", "gray.900/50")}
					/>
				</InputGroup>

				<HStack spacing={4} w={{ base: "100%", md: "auto" }}>
					<Select
						w="150px"
						borderRadius="xl"
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						bg={useColorModeValue("gray.50", "gray.900/50")}
					>
						<option value="All">All Statuses</option>
						<option value="Pending">Pending</option>
						<option value="Resolved">Resolved</option>
					</Select>

					<Select
						w="160px"
						borderRadius="xl"
						value={categoryFilter}
						onChange={(e) => setCategoryFilter(e.target.value)}
						bg={useColorModeValue("gray.50", "gray.900/50")}
					>
						<option value="All">All Categories</option>
						<option value="bug">Bugs / Glitches</option>
						<option value="video">Video Playback</option>
						<option value="quiz">Quiz Sync</option>
						<option value="content">Curriculum</option>
						<option value="other">General</option>
					</Select>
				</HStack>
			</Flex>

			{/* Tickets Table */}
			<Box borderRadius="2xl" bg={bg} border="1px solid" borderColor={borderColor} shadow="sm" overflow="hidden">
				{isLoading ? (
					<Flex justify="center" align="center" py={20} direction="column" gap={4}>
						<Spinner size="xl" color="green.500" thickness="3px" />
						<Text fontSize="sm" color="gray.500">Connecting to DynamoDB support tickets...</Text>
					</Flex>
				) : (
					<Table variant="simple" size="md">
						<ThemedThead bg={useColorModeValue("gray.50", "gray.900")}>
							<Tr>
								<Th>Ticket Ref</Th>
								<Th>User / Email</Th>
								<Th>Category</Th>
								<Th>Subject</Th>
								<Th>Device Info</Th>
								<Th>Status</Th>
								<Th>Created</Th>
								<Th textAlign="right">Actions</Th>
							</Tr>
						</ThemedThead>
						<Tbody>
							{filteredTickets.length === 0 ? (
								<Tr>
									<Td colSpan={8} textAlign="center" py={12} color="gray.500">
										<VStack spacing={2}>
											<FontAwesomeIcon icon={faInbox} size="2x" color="gray" />
											<Text fontWeight="medium">No support tickets found.</Text>
											<Text fontSize="xs" color="gray.400">Reports submitted from the mobile app will automatically appear here in real time.</Text>
										</VStack>
									</Td>
								</Tr>
							) : (
								filteredTickets.map((ticket) => (
									<Tr
										key={ticket.id}
										_hover={{ bg: useColorModeValue("gray.50/80", "gray.750") }}
										transition="all 0.15s"
										cursor="pointer"
										onClick={() => handleViewTicket(ticket)}
									>
										<Td fontWeight="bold" fontSize="xs" color="blue.500" fontFamily="monospace">
											#{ticket.id}
										</Td>
										<Td>
											<VStack align="left" spacing={0.5}>
												<Text fontWeight="bold" fontSize="sm">{ticket.name || 'Learner'}</Text>
												<Text fontSize="xs" color="gray.500">{ticket.email}</Text>
											</VStack>
										</Td>
										<Td>
											{getCategoryBadge(ticket.category)}
										</Td>
										<Td maxW="240px">
											<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
												{ticket.subject}
											</Text>
										</Td>
										<Td>
											<HStack spacing={1}>
												<FontAwesomeIcon icon={faMobileScreen} color="gray" style={{ fontSize: '11px' }} />
												<Text fontSize="xs" color="gray.500">
													{ticket.deviceOs || 'iOS'} {ticket.deviceVersion || ''}
												</Text>
											</HStack>
										</Td>
										<Td>
											<Badge
												colorScheme={ticket.status === "Pending" ? "orange" : "green"}
												variant="subtle"
												borderRadius="full"
												px={2.5}
												py={0.5}
												fontSize="xs"
											>
												{ticket.status}
											</Badge>
										</Td>
										<Td fontSize="xs" color="gray.500">
											{new Date(ticket.createdAt).toLocaleDateString()}
										</Td>
										<Td textAlign="right">
											<HStack spacing={2} justify="flex-end">
												<Tooltip label="View Details & Reply">
													<IconButton
														aria-label="View Details"
														icon={<FontAwesomeIcon icon={faEye} />}
														size="sm"
														variant="ghost"
														colorScheme="blue"
														onClick={(e) => {
															e.stopPropagation();
															handleViewTicket(ticket);
														}}
													/>
												</Tooltip>
												<Tooltip label="Delete Ticket">
													<IconButton
														aria-label="Delete Ticket"
														icon={<FontAwesomeIcon icon={faTrash} />}
														size="sm"
														variant="ghost"
														colorScheme="red"
														onClick={(e) => handleDeleteClick(ticket.id, e)}
													/>
												</Tooltip>
											</HStack>
										</Td>
									</Tr>
								))
							)}
						</Tbody>
					</Table>
				)}
			</Box>

			{/* Ticket Details & Reply Modal */}
			{selectedTicket && (
				<Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
					<ModalOverlay backdropFilter="blur(8px)" bg="rgba(15, 23, 42, 0.4)" />
					<ModalContent borderRadius="2xl" border="1px solid" borderColor={borderColor} bg={bg} shadow="2xl" p={2}>
						<ModalHeader display="flex" alignItems="center" gap={3} fontWeight="bold" borderBottom="1px solid" borderColor={borderColor}>
							<Box display="flex" alignItems="center" justifyContent="center" boxSize="36px" borderRadius="full" bg={selectedTicket.status === "Pending" ? "orange.50" : "green.50"} color={selectedTicket.status === "Pending" ? "orange.500" : "green.500"} _dark={{ bg: selectedTicket.status === "Pending" ? "rgba(237,137,54,0.15)" : "rgba(72,187,120,0.15)" }}>
								<FontAwesomeIcon icon={faLifeRing} />
							</Box>
							Support Ticket - #{selectedTicket.id}
						</ModalHeader>
						<ModalCloseButton borderRadius="full" m={2} />
						<ModalBody py={6}>
							<VStack spacing={5} align="stretch">
								<SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
									<FormControl isReadOnly>
										<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">From User</FormLabel>
										<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={`${selectedTicket.name} (${selectedTicket.email})`} />
									</FormControl>
									<FormControl isReadOnly>
										<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Category</FormLabel>
										<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={selectedTicket.category.toUpperCase()} />
									</FormControl>
									<FormControl isReadOnly>
										<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Device Diagnostic</FormLabel>
										<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={`${selectedTicket.deviceOs || 'iOS'} (${selectedTicket.deviceVersion || 'Current'})`} />
									</FormControl>
								</SimpleGrid>

								<FormControl isReadOnly>
									<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Subject / Issue Summary</FormLabel>
									<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={selectedTicket.subject} />
								</FormControl>

								<FormControl isReadOnly>
									<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Description & Steps</FormLabel>
									<Textarea size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={selectedTicket.description} rows={5} />
								</FormControl>

								<Divider borderColor={borderColor} />

								{/* Reply Section */}
								{selectedTicket.status === "Pending" ? (
									<FormControl isRequired>
										<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Admin Response</FormLabel>
										<Textarea
											size="sm"
											borderRadius="lg"
											placeholder="Type your resolution details here. This message will automatically be emailed to the user from support@xnav.studio and sent as a push notification to their device."
											rows={4}
											value={replyMessage}
											onChange={(e) => setReplyMessage(e.target.value)}
										/>
									</FormControl>
								) : (
									<VStack align="stretch" spacing={2} p={4} bg={useColorModeValue("green.50/50", "rgba(72,187,120,0.1)")} borderRadius="xl" border="1px solid" borderColor="green.200">
										<Text fontSize="xs" fontWeight="bold" color="green.600" textTransform="uppercase">Response Sent to Student</Text>
										<Text fontSize="sm" fontStyle="italic" color={useColorModeValue("gray.700", "gray.300")}>
											"{selectedTicket.reply}"
										</Text>
										{selectedTicket.resolvedAt && (
											<Text fontSize="10px" color="gray.400">
												Resolved on: {new Date(selectedTicket.resolvedAt).toLocaleString()} by {selectedTicket.resolvedBy || 'Admin'}
											</Text>
										)}
									</VStack>
								)}
							</VStack>
						</ModalBody>
						<ModalFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
							<Button size="sm" borderRadius="full" variant="ghost" onClick={onClose}>Close</Button>
							{selectedTicket.status === "Pending" && (
								<Button
									size="sm"
									borderRadius="full"
									colorScheme="green"
									px={5}
									leftIcon={<FontAwesomeIcon icon={faReply} />}
									isLoading={isSubmittingReply}
									onClick={handleSendReply}
								>
									Resolve &amp; Notify User
								</Button>
							)}
						</ModalFooter>
					</ModalContent>
				</Modal>
			)}

			{/* Delete Ticket Confirmation Alert */}
			<AlertDialog
				isOpen={isDeleteOpen}
				leastDestructiveRef={cancelRef}
				onClose={onDeleteClose}
			>
				<AlertDialogOverlay backdropFilter="blur(8px)" bg="rgba(15, 23, 42, 0.4)">
					<AlertDialogContent borderRadius="2xl" border="1px solid" borderColor={borderColor} bg={bg} p={2}>
						<AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={3} borderBottom="1px solid" borderColor={borderColor} pb={3}>
							<Box display="flex" alignItems="center" justifyContent="center" boxSize="36px" borderRadius="full" bg="red.50" color="red.500" _dark={{ bg: "rgba(239, 68, 68, 0.15)" }}>
								<FontAwesomeIcon icon={faExclamationTriangle} />
							</Box>
							Delete Support Ticket
						</AlertDialogHeader>

						<AlertDialogBody py={6} fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }} fontWeight="medium">
							Are you sure you want to delete this support ticket from AWS? This action is permanent and cannot be undone.
						</AlertDialogBody>

						<AlertDialogFooter borderTop="1px solid" borderColor={borderColor} pt={3} gap={3}>
							<Button ref={cancelRef} onClick={onDeleteClose} size="sm" borderRadius="full" variant="ghost">
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleDeleteConfirm} size="sm" borderRadius="full" px={5}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}

// Custom Thead wrapper with type support
function ThemedThead({ children, ...props }: any) {
	return <Thead {...props}>{children}</Thead>;
}
